import { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { MutationErrorCollection } from '@atlassian/jira-mutation-error/src/mutation-error-collection.tsx';
import type { setJqlMutation } from './__generated__/setJqlMutation.graphql';

export const useSetJql = () => {
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<setJqlMutation>(graphql`
		mutation setJqlMutation($input: JiraSetIssueSearchJqlInput!) {
			jira_setIssueSearchJql(input: $input) {
				success
				errors {
					message
					extensions {
						statusCode
						errorType
					}
				}
			}
		}
	`);

	return useCallback(
		({ jql, viewARI }: { jql: string; viewARI: string }) => {
			const handleError = (error: Error) => {
				fireErrorAnalytics({
					sendToPrivacyUnsafeSplunk: true,
					error,
					meta: {
						id: 'setJqlMutation',
						packageName: 'jiraIssueNavigator',
						teamName: 'empanada',
					},
				});
			};

			commit({
				variables: {
					input: {
						jql,
						viewId: viewARI,
					},
				},
				onError: handleError,
				onCompleted(response) {
					if (response.jira_setIssueSearchJql?.success !== true) {
						const error = response.jira_setIssueSearchJql?.errors?.[0];
						const statusCode = error?.extensions?.statusCode ?? 0;

						const isClientError = statusCode >= 400 && statusCode < 500;

						/*
						 * The mutation will return a validation error if the provided JQL string is not syntactically correct.
						 * This is already handled by the filter component, so we don't need to handle it here.
						 */
						if (!isClientError) {
							const mutationError = new MutationErrorCollection(
								response.jira_setIssueSearchJql?.errors,
							);

							handleError(mutationError);
						}
					}
				},
			});
		},
		[commit],
	);
};
